import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';
import {
  parseBreakdown,
  sizelessBreakdown,
  expandCasepack,
  casepackAvailability,
  addCasepacks,
} from '@theme/casepack';

/**
 * @module casepack-pdp
 *
 * Product-page UI for the casepack build (phase 2). The PDP sells whole
 * casepacks of one color: the variant picker shows Color only, the breakdown is
 * information text, the stepper counts casepacks rather than units, and the
 * price is the unit price times the units in a pack.
 *
 * All casepack maths lives in `@theme/casepack`; this module is DOM + network
 * glue only. Two custom elements are defined:
 *
 *   - `<casepack-refresh-component>` — re-renders itself from the document the
 *     variant picker fetched when the color changes. Used by the price and
 *     breakdown blocks, which are plain server-rendered Liquid.
 *   - `<casepack-form-component>` — the stepper plus add-to-cart button. Expands
 *     the chosen casepack count into per-size line items and writes the cart.
 */

/**
 * Find this element's replacement in a re-rendered document and morph it in.
 * The PDP variant picker fetches the whole product page, so every block can
 * update itself by id without a second request.
 *
 * @param {HTMLElement} element  Must carry a stable `id`.
 * @param {Document|null|undefined} html  The re-rendered document.
 * @returns {boolean} True when a replacement was found and applied.
 */
function morphFromDocument(element, html) {
  if (!element.id || !html) return false;

  const replacement = html.getElementById(element.id);
  if (!replacement) return false;

  morph(element, replacement);
  return true;
}

/**
 * Subscribe to the variant picker's `shopify:product:select` for the product
 * this element belongs to. Returns a teardown function.
 *
 * The event is dispatched on the `<variant-picker>` and bubbles, so we listen on
 * the nearest shared ancestor rather than on the picker itself (which may be
 * morphed away). Events for other products — a recommendation card, a quick-add
 * dialog — are filtered out by product id.
 *
 * @param {HTMLElement} element
 * @param {(detail: any) => void} onSelect  Called with the resolved event detail.
 * @returns {() => void}
 */
function onProductSelect(element, onSelect) {
  const scope = element.closest('.shopify-section') ?? document;

  /** @param {any} event */
  const handler = (event) => {
    // Ignore pickers belonging to a different product on the same page.
    const productId = element.dataset.productId;
    const eventProductId = event.product?.id ? String(event.product.id).split('/').pop() : '';
    if (productId && eventProductId && eventProductId !== productId) return;
    if (element.closest('product-card, quick-add-dialog')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail) return;
        if (productId && detail.productId && String(detail.productId) !== productId) return;
        onSelect(detail);
      })
      .catch((/** @type {any} */ error) => {
        if (error?.name !== 'AbortError') console.warn('[casepack] product select rejected:', error);
      });
  };

  scope.addEventListener(StandardEvents.productSelect, handler);
  return () => scope.removeEventListener(StandardEvents.productSelect, handler);
}

/**
 * A server-rendered block that re-renders itself when the selected color
 * changes. Carries no casepack logic of its own — the Liquid it wraps is the
 * source of truth, so price formatting and translated copy stay in Liquid.
 */
class CasepackRefreshComponent extends Component {
  /** @type {(() => void) | null} */
  #teardown = null;

  connectedCallback() {
    super.connectedCallback();
    this.#teardown = onProductSelect(this, (detail) => {
      morphFromDocument(this, detail.html);
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#teardown?.();
    this.#teardown = null;
  }
}

/**
 * @typedef {Object} CasepackSize
 * @property {string} name
 * @property {number} id         Variant id.
 * @property {number|null} inventory  Null when untracked or overselling is allowed.
 */

/**
 * @typedef {Object} CasepackData
 * @property {string} handle
 * @property {string} color
 * @property {string} breakdown  Raw "Other" option value, e.g. "1-2-2-1".
 * @property {boolean} sizeless
 * @property {boolean} valid     Liquid's own view of well-formedness.
 * @property {number} unitPrice
 * @property {number} packPrice
 * @property {CasepackSize[]} sizes
 */

/**
 * @typedef {Object} Refs
 * @property {HTMLScriptElement} casepackData
 * @property {HTMLInputElement} quantityInput
 * @property {HTMLButtonElement} minusButton
 * @property {HTMLButtonElement} plusButton
 * @property {HTMLButtonElement} addButton
 * @property {HTMLElement} [errorText]
 * @property {HTMLElement} [liveRegion]
 * @extends {Component<Refs>}
 */
class CasepackFormComponent extends Component {
  requiredRefs = ['casepackData', 'quantityInput', 'minusButton', 'plusButton', 'addButton'];

  /** @type {(() => void) | null} */
  #teardown = null;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #errorTimeout;
  #submitting = false;

  connectedCallback() {
    super.connectedCallback();
    this.#syncConstraints();
    this.#teardown = onProductSelect(this, (detail) => {
      if (!morphFromDocument(this, detail.html)) return;
      // Refs are refreshed by the base class's MutationObserver, which runs after
      // the morph. Sync on the next frame so we read the new color's data.
      requestAnimationFrame(() => this.#syncConstraints());
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#teardown?.();
    this.#teardown = null;
    if (this.#errorTimeout) clearTimeout(this.#errorTimeout);
  }

  /** Re-read refs and constraints after the morph layer swaps our subtree in. */
  updatedCallback() {
    super.updatedCallback?.();
    this.#syncConstraints();
  }

  /**
   * The casepack payload Liquid rendered for the currently selected color.
   * @returns {CasepackData|null}
   */
  get #data() {
    try {
      return JSON.parse(this.refs.casepackData.textContent || '');
    } catch (error) {
      console.warn('[casepack] could not parse casepack data:', error);
      return null;
    }
  }

  /**
   * Breakdown for the current color, from the raw option string. Sizeless styles
   * are a single variant bought a full pack deep.
   * @param {CasepackData} data
   */
  #breakdown(data) {
    return data.sizeless ? sizelessBreakdown() : parseBreakdown(data.breakdown, data.sizes.length);
  }

  /**
   * Whole casepacks buyable for this color, capped by per-size inventory.
   * Untracked sizes arrive as `null` and are treated as unbounded.
   * @param {CasepackData} data
   * @param {{units: number[]}} breakdown
   * @returns {number}
   */
  #availability(data, breakdown) {
    const inventories = data.sizes.map((size) => (size.inventory === null ? Infinity : Number(size.inventory) || 0));
    return casepackAvailability({ inventories, units: breakdown.units });
  }

  /**
   * Apply the current color's availability to the stepper and button, and
   * disable the whole control when the breakdown can't be trusted.
   */
  #syncConstraints() {
    const { quantityInput, addButton, minusButton, plusButton } = this.refs;
    const data = this.#data;

    if (!data) {
      this.#setBuyable(false);
      return;
    }

    const breakdown = this.#breakdown(data);
    if (!breakdown.valid || !data.valid) {
      // Malformed or missing breakdown: never guess a pack shape, just refuse.
      console.warn(`[casepack] unusable breakdown for ${data.handle}|${data.color}:`, breakdown.reason);
      this.#setBuyable(false);
      this.#showError(this.dataset.errorBreakdown);
      return;
    }

    const available = this.#availability(data, breakdown);
    this.#setBuyable(available >= 1);

    if (available < 1) {
      this.#showError(this.dataset.errorSoldOut);
      return;
    }

    quantityInput.min = '1';
    if (available === Infinity) quantityInput.removeAttribute('max');
    else quantityInput.max = String(available);

    const clamped = this.#clamp(Number(quantityInput.value) || 1, available);
    quantityInput.value = String(clamped);
    minusButton.disabled = clamped <= 1;
    plusButton.disabled = available !== Infinity && clamped >= available;
    addButton.disabled = false;
  }

  /**
   * @param {number} value
   * @param {number} max
   */
  #clamp(value, max) {
    const packs = Math.max(1, Math.trunc(value) || 1);
    return max === Infinity ? packs : Math.min(packs, Math.max(1, max));
  }

  /** @param {boolean} buyable */
  #setBuyable(buyable) {
    const { quantityInput, addButton, minusButton, plusButton } = this.refs;
    quantityInput.disabled = !buyable;
    addButton.disabled = !buyable;
    minusButton.disabled = !buyable;
    plusButton.disabled = !buyable;
  }

  /** Step the casepack count. @param {number} delta */
  #step(delta) {
    const data = this.#data;
    if (!data) return;

    const breakdown = this.#breakdown(data);
    if (!breakdown.valid) return;

    const { quantityInput } = this.refs;
    const available = this.#availability(data, breakdown);
    quantityInput.value = String(this.#clamp((Number(quantityInput.value) || 1) + delta, available));
    this.#syncConstraints();
  }

  increaseQuantity() {
    this.#step(1);
  }

  decreaseQuantity() {
    this.#step(-1);
  }

  /** Normalise typed input on blur — the field only ever holds whole casepacks. */
  setQuantity() {
    this.#syncConstraints();
  }

  selectInputValue() {
    this.refs.quantityInput.select();
  }

  /**
   * Expand the chosen casepack count into per-size line items and add them in a
   * single cart write, then hand the cart drawer the same event shape the stock
   * product form emits so it opens and re-renders unchanged.
   *
   * @param {Event} event
   */
  async handleSubmit(event) {
    event.preventDefault();
    if (this.#submitting) return;

    const data = this.#data;
    if (!data) return;

    const breakdown = this.#breakdown(data);
    if (!breakdown.valid || !data.valid) {
      this.#showError(this.dataset.errorBreakdown);
      return;
    }

    const packCount = this.#clamp(Number(this.refs.quantityInput.value) || 1, this.#availability(data, breakdown));

    /** @type {import('@theme/casepack').CasepackLine[]} */
    let lines;
    try {
      lines = expandCasepack({
        handle: data.handle,
        color: data.color,
        variantIds: data.sizes.map((size) => size.id),
        units: breakdown.units,
        packCount,
      });
    } catch (error) {
      console.warn('[casepack] could not expand casepack:', error);
      this.#showError(this.dataset.errorBreakdown);
      return;
    }

    this.#submitting = true;
    this.refs.addButton.disabled = true;
    this.#clearError();

    const sectionIds = this.#cartSectionIds();
    const totalUnits = lines.reduce((sum, line) => sum + line.quantity, 0);
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: lines.map((line) => ({ merchandiseId: String(line.id), quantity: line.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await addCasepacks(lines, sectionIds ? { sections: sectionIds } : {});
      const cart = await this.#refreshCart();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'casepack-form-component',
          sourceId: this.id,
          itemCount: totalUnits,
          productId: this.dataset.productId,
          sections: response.sections,
          didError: false,
        },
      });

      this.#announce(this.dataset.addedText);
    } catch (error) {
      const message = /** @type {any} */ (error)?.cartError?.description || /** @type {any} */ (error)?.message;
      const isCartError = Boolean(/** @type {any} */ (error)?.cartError);

      this.dispatchEvent(
        new CartErrorEvent({
          error: message || 'Add to cart failed',
          code: isCartError ? 'INVALID' : 'SERVICE_UNAVAILABLE',
        })
      );

      // Resolve rather than reject so the drawer stops its optimistic open and
      // shows the real cart, matching the stock product form's error path.
      this.#refreshCart()
        .then((cart) =>
          deferred.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: {
              items: cart.items,
              source: 'casepack-form-component',
              sourceId: this.id,
              itemCount: totalUnits,
              productId: this.dataset.productId,
              didError: true,
            },
          })
        )
        .catch(deferred.reject);

      this.#showError(message || this.dataset.errorGeneric);
    } finally {
      this.#submitting = false;
      this.#syncConstraints();
    }
  }

  /**
   * Section ids of every cart renderer on the page, so the add response carries
   * the HTML `<cart-items-component>` needs to re-render itself.
   * @returns {string}
   */
  #cartSectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => /** @type {HTMLElement} */ (element).dataset.sectionId)
      .filter(Boolean)
      .join(',');
  }

  /** @returns {Promise<any>} */
  async #refreshCart() {
    const cartItems = document.querySelector('cart-items-component');

    if (cartItems) {
      await customElements.whenDefined('cart-items-component');
      return /** @type {any} */ (cartItems).fetchCartData();
    }

    const response = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  }

  /** @param {string} [message] */
  #showError(message) {
    const { errorText } = this.refs;
    if (!errorText || !message) return;

    errorText.textContent = message;
    errorText.classList.remove('hidden');
    this.#announce(message);

    if (this.#errorTimeout) clearTimeout(this.#errorTimeout);
    this.#errorTimeout = setTimeout(() => this.#clearError(), 10000);
  }

  #clearError() {
    const { errorText } = this.refs;
    if (!errorText) return;
    errorText.textContent = '';
    errorText.classList.add('hidden');
  }

  /** @param {string} [message] */
  #announce(message) {
    const { liveRegion } = this.refs;
    if (!liveRegion || !message) return;
    liveRegion.textContent = message;
    setTimeout(() => {
      if (liveRegion.textContent === message) liveRegion.textContent = '';
    }, 3000);
  }
}

if (!customElements.get('casepack-refresh-component')) {
  customElements.define('casepack-refresh-component', CasepackRefreshComponent);
}

if (!customElements.get('casepack-form-component')) {
  customElements.define('casepack-form-component', CasepackFormComponent);
}
